import { useContext, useState } from "react";
import IconButton from "@mui/material/IconButton";
import DeleteOutlineOutlinedIcon from "@mui/icons-material/DeleteOutlineOutlined";
import {
  Autocomplete,
  Box,
  Button,
  Collapse,
  Grid,
  Paper,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import BuildIcon from "@mui/icons-material/Build";
import NorthEastIcon from "@mui/icons-material/NorthEast";
import SouthEastIcon from "@mui/icons-material/SouthEast";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import styles from "../components.module.sass";
import DesignerContext from "../../context/DesignerContext";
import { pulling_details_line } from "../../utils/data";
import { ImageProps, ProductProps } from "../../interfaces/interfaces";
import DataContext from "../../context/DataContext";
import { TitlebarImageList } from "../common/ImageList";
import AddPhotoAlternateIcon from "@mui/icons-material/AddPhotoAlternate";
import AddCommentIcon from "@mui/icons-material/AddComment";

export const PullingDetailsTable = () => {
  const { pullingInfo, onEditPullingInfo } = useContext(DesignerContext);
  const { productOptions } = useContext(DataContext);

  // indices in this Set are collapsed; everything else is expanded
  const [collapsed, setCollapsed] = useState<Set<number>>(new Set());

  const toggleCollapse = (index: number) =>
    setCollapsed((prev) => {
      const next = new Set(prev);
      next.has(index) ? next.delete(index) : next.add(index);
      return next;
    });

  // ── Handlers ──────────────────────────────────────────────────────────────

  const handleAddLine = (index: number) => {
    const newDetails =
      index != null
        ? [
            ...pullingInfo.details.slice(0, index),
            pulling_details_line,
            ...pullingInfo.details.slice(index),
          ]
        : [...pullingInfo.details, pulling_details_line];
    onEditPullingInfo((prev) => ({ ...prev, details: newDetails }));
  };

  const handleRemoveLine = (index: number) => {
    onEditPullingInfo((prev) => ({
      ...prev,
      details: prev.details.filter((_, i) => i !== index),
    }));
  };

  const handleEditLine = (index: number, name: string, value: any) => {
    onEditPullingInfo((prev) => ({
      ...prev,
      details: prev.details.map((line, i) =>
        i === index ? { ...line, [name]: value } : line
      ),
    }));
  };

  const handleEditDetailLine = (
    indexLine: number,
    indexDetail: number,
    value: string
  ) => {
    onEditPullingInfo((prev) => ({
      ...prev,
      details: prev.details.map((detail, i) =>
        i === indexLine
          ? {
              ...detail,
              comment: detail.comment.map((c, ci) =>
                ci === indexDetail ? value : c
              ),
            }
          : detail
      ),
    }));
  };

  const handleRemoveComment = (indexLine: number, indexComment: number) => {
    onEditPullingInfo((prev) => ({
      ...prev,
      details: prev.details.map((detail, i) =>
        i === indexLine
          ? { ...detail, comment: detail.comment.filter((_, ci) => ci !== indexComment) }
          : detail
      ),
    }));
  };

  const handleDeleteImage = (indexLine: number, indexImage: number) => {
    onEditPullingInfo((prev) => ({
      ...prev,
      details: prev.details.map((detail, i) =>
        i === indexLine
          ? { ...detail, pictures: detail.pictures.filter((_, pi) => pi !== indexImage) }
          : detail
      ),
    }));
  };

  const handleEditImagelLine = (
    indexLine: number,
    indexDetail: number,
    value: ImageProps
  ) => {
    onEditPullingInfo((prev) => ({
      ...prev,
      details: prev.details.map((detail, i) =>
        i === indexLine
          ? {
              ...detail,
              pictures: detail.pictures.map((p, pi) =>
                pi === indexDetail ? value : p
              ),
            }
          : detail
      ),
    }));
  };

  const handleAddItem = (indexLine: number, name: "pictures" | "comment") => {
    onEditPullingInfo((prev) => ({
      ...prev,
      details: prev.details.map((detail, i) =>
        i === indexLine
          ? { ...detail, [name]: [...detail[name], ""] }
          : detail
      ),
    }));
  };

  const handleAddPicture = (indexLine: number) => {
    onEditPullingInfo((prev) => ({
      ...prev,
      details: prev.details.map((detail, i) =>
        i === indexLine
          ? {
              ...detail,
              pictures: [
                ...detail.pictures,
                { image: "", size: 1, scale: 1, rotate: 0 },
              ],
            }
          : detail
      ),
    }));
  };

  // ── Autocomplete option props ──────────────────────────────────────────────

  const byPartNumber = {
    options: productOptions,
    getOptionLabel: (o: ProductProps) => o.partNumber,
  };

  const byName = {
    options: productOptions,
    getOptionLabel: (o: ProductProps) => o.name,
  };

  // ── Render ─────────────────────────────────────────────────────────────────

  return (
    <>
      {/* Add first line button */}
      <div className={styles.buttonTally}>
        <Button
          variant="outlined"
          startIcon={<BuildIcon fontSize="small" />}
          onClick={() => handleAddLine(pullingInfo.details.length)}
        >
          Add line
        </Button>
      </div>

      {/* Card list — one card per tool row */}
      <Stack spacing={2} sx={{ px: 2, pb: 2 }}>
        {pullingInfo.details.map((item, index) => (
          <Paper key={index} variant="outlined" sx={{ borderRadius: 2, overflow: "hidden" }}>

            {/* ── Card header ── */}
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                px: 2,
                py: 0.75,
                bgcolor: "rgb(140, 150, 169)",
                color: "#fff",
                cursor: "pointer",
                userSelect: "none",
              }}
              onClick={() => toggleCollapse(index)}
            >
              {/* Left: number + summary when collapsed */}
              <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, minWidth: 0 }}>
                <Typography variant="subtitle2" fontWeight="bold" sx={{ whiteSpace: "nowrap" }}>
                  Tool #{index + 1}
                </Typography>
                {collapsed.has(index) && item.description.id !== 0 && (
                  <Typography
                    variant="caption"
                    sx={{
                      opacity: 0.9,
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {[item.description.partNumber, item.description.name]
                      .filter(Boolean)
                      .join(" — ")}
                  </Typography>
                )}
              </Box>

              {/* Right: collapse toggle + action buttons */}
              <Box sx={{ display: "flex", gap: 0.25, flexShrink: 0 }} onClick={(e) => e.stopPropagation()}>
                <Tooltip title="Insert above">
                  <IconButton
                    size="small"
                    onClick={() => handleAddLine(index)}
                    sx={{ color: "#FBAB53" }}
                  >
                    <NorthEastIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Insert below">
                  <IconButton
                    size="small"
                    onClick={() => handleAddLine(index + 1)}
                    sx={{ color: "#FBAB53" }}
                  >
                    <SouthEastIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Delete tool">
                  <IconButton
                    size="small"
                    onClick={() => handleRemoveLine(index)}
                    sx={{ color: "#ffaaaa" }}
                  >
                    <DeleteOutlineOutlinedIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
                <Tooltip title={collapsed.has(index) ? "Expand" : "Collapse"}>
                  <IconButton
                    size="small"
                    onClick={() => toggleCollapse(index)}
                    sx={{ color: "#fff", ml: 0.5 }}
                  >
                    {collapsed.has(index)
                      ? <ExpandMoreIcon fontSize="small" />
                      : <ExpandLessIcon fontSize="small" />}
                  </IconButton>
                </Tooltip>
              </Box>
            </Box>

            {/* ── Card body (collapsible) ── */}
            <Collapse in={!collapsed.has(index)} timeout="auto" unmountOnExit>
            <Box sx={{ p: 2 }}>
              <Grid container spacing={2}>

                {/* Description */}
                <Grid item xs={12} md={3}>
                  <Typography
                    variant="overline"
                    color="text.secondary"
                    sx={{ fontWeight: 600, display: "block", mb: 1 }}
                  >
                    Description
                  </Typography>
                  <Stack spacing={1.5}>
                    <Autocomplete
                      value={item.description}
                      isOptionEqualToValue={() => true}
                      onChange={(_, v) =>
                        v && handleEditLine(index, "description", v)
                      }
                      renderOption={(props, option) => (
                        <li {...props} key={option.id}>
                          {option.partNumber}
                        </li>
                      )}
                      {...byPartNumber}
                      disablePortal
                      size="small"
                      renderInput={(params) => (
                        <TextField {...params} label="Part Number" />
                      )}
                    />
                    <Autocomplete
                      value={item.description}
                      isOptionEqualToValue={() => true}
                      onChange={(_, v) =>
                        v && handleEditLine(index, "description", v)
                      }
                      renderOption={(props, option) => (
                        <li {...props} key={option.id}>
                          {option.name}
                        </li>
                      )}
                      {...byName}
                      disablePortal
                      size="small"
                      renderInput={(params) => (
                        <TextField {...params} label="Description" />
                      )}
                    />
                    <TextField
                      label="Tool Name"
                      size="small"
                      fullWidth
                      value={item.toolName}
                      onChange={(e) =>
                        handleEditLine(index, "toolName", e.target.value)
                      }
                    />
                    <TextField
                      label="Quantity"
                      type="number"
                      size="small"
                      fullWidth
                      value={item.quantity}
                      inputProps={{ min: 1 }}
                      onChange={(e) =>
                        parseInt(e.target.value) >= 1 &&
                        handleEditLine(index, "quantity", e.target.value)
                      }
                    />
                  </Stack>
                </Grid>

                {/* Evidences */}
                <Grid item xs={12} md={5}>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                    }}
                  >
                    <Typography
                      variant="overline"
                      color="text.secondary"
                      sx={{ fontWeight: 600 }}
                    >
                      Evidences (Photos)
                    </Typography>
                    <Tooltip title="Add photo slot">
                      <IconButton
                        size="small"
                        onClick={() => handleAddPicture(index)}
                        sx={{ color: "green" }}
                      >
                        <AddPhotoAlternateIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </Box>
                  <TitlebarImageList
                    itemData={item.pictures}
                    index={index}
                    onChange={handleEditImagelLine}
                    handleDeleteImage={handleDeleteImage}
                  />
                </Grid>

                {/* Comments */}
                <Grid item xs={12} md={4}>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                    }}
                  >
                    <Typography
                      variant="overline"
                      color="text.secondary"
                      sx={{ fontWeight: 600 }}
                    >
                      Comments
                    </Typography>
                    <Tooltip title="Add comment">
                      <IconButton
                        size="small"
                        onClick={() => handleAddItem(index, "comment")}
                        sx={{ color: "green" }}
                      >
                        <AddCommentIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </Box>
                  <Stack spacing={1} sx={{ mt: 1 }}>
                    {item.comment.map((comment, idx) => (
                      <Box
                        key={idx}
                        sx={{ display: "flex", alignItems: "flex-start", gap: 0.5 }}
                      >
                        <TextField
                          value={comment}
                          multiline
                          rows={3}
                          size="small"
                          fullWidth
                          onChange={(e) =>
                            handleEditDetailLine(index, idx, e.target.value)
                          }
                        />
                        <Tooltip title="Remove comment">
                          <IconButton
                            size="small"
                            onClick={() => handleRemoveComment(index, idx)}
                            sx={{ mt: 0.5 }}
                          >
                            <DeleteOutlineOutlinedIcon
                              sx={{ color: "#CD1719" }}
                              fontSize="small"
                            />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    ))}
                  </Stack>
                </Grid>

              </Grid>
            </Box>
            </Collapse>
          </Paper>
        ))}
      </Stack>
    </>
  );
};
